import { FC, useContext, useEffect, useState } from 'react'
import { Base64File, Base64FileInput, Button, Header, SelectInput } from '../Generic'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { residenceProofType } from '@methods/getOnboardingContext'
import { SelectOption } from '../Generic/SelectInput/SelectInput'
import { API_URL } from '@/constants/apiUrl'
import { cx } from '@/utils/cx'
interface DefaultScenarioProps {
  setLoading: (loading: boolean) => void
}

export const DefaultScenario: FC<DefaultScenarioProps> = observer(({ setLoading }) => {
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { context } = store.ScenarioState
  const { language } = store.TranslationsState
  const { pixelSpeak: trans } = store.TranslationsState.translations

  const [fileSelected, setFileSelected] = useState<Base64File>()
  const [fileError, setFileError] = useState('')
  const [fileInputKey, setFileInputKey] = useState(Date.now())
  const [isFileBeingDragged, setIsFileBeingDragged] = useState(false)

  const [isDisabled, setIsDisabled] = useState(true)
  const [utilityBillType, setUtilityBillType] = useState<{
    value: string
    label: string
  } | null>(null)
  const [utilityBillTypeId, setUtilityBillTypeId] = useState(0)

  const allDocumentTypes: Record<string, string> = {
    ...trans.documentTypes,
    ...trans.payslipDocumentTypes,
  }
  const documentTypesOptions = Object.keys(allDocumentTypes)
    .filter((item) => context?.availableResidenceProofTypes?.includes(item as residenceProofType))
    .map((key) => ({
      value: key,
      label: allDocumentTypes[key],
    }))

  useEffect(() => {
    setUtilityBillType(documentTypesOptions[utilityBillTypeId])
  }, [language])

  const resetFileInput = () => {
    setFileInputKey(Date.now())
    setFileSelected(undefined)
    setFileError('')
    setIsDisabled(true)
    setIsFileBeingDragged(false)
  }

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsFileBeingDragged(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsFileBeingDragged(false)
  }

  useEffect(() => {
    document.addEventListener('dragover', handleDragEnter, false)
    document.addEventListener('dragleave', handleDragLeave, false)

    return () => {
      document.removeEventListener('dragover', handleDragEnter, false)
      document.removeEventListener('dragleave', handleDragLeave, false)
    }
  })

  const handleProceed = async () => {
    if (isDisabled) {
      setFileError(trans.fileError)
    } else {
      setLoading(true)
      await handleFile()

      forceRedirect(`${API_URL}/pixel-speak/complete`)
    }
  }

  const getFiles = async (file: Base64File) => {
    setFileSelected(file)
  }

  const handleFile = async () => {
    if (!context?.documentUploadUrl) return

    try {
      await axiosInstance.post(
        context.documentUploadUrl,
        {
          file: fileSelected?.base64,
          type: utilityBillType?.value,
          fileName: fileSelected?.name,
        },
        {
          withCredentials: true,
        }
      )
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    setIsDisabled(true)
    if (fileSelected?.base64) {
      if (
        fileSelected.type !== 'application/pdf' &&
        fileSelected.type !== 'image/jpeg' &&
        fileSelected.type !== 'image/png'
      ) {
        setFileError(trans.fileErrorType)
      } else if (fileSelected.sizeMB >= 5) {
        setFileError(trans.fileErrorSize)
      } else {
        setFileError('')
        setIsDisabled(false)
      }
    } else {
      resetFileInput()
    }
  }, [fileSelected, fileSelected?.base64])

  const selectChangeHandle = (e: SelectOption) => {
    const index = documentTypesOptions.findIndex((item) => item.value === e.value)
    setUtilityBillType(e)
    setUtilityBillTypeId(index)
  }

  useEffect(() => {
    devLog('fileSelected', fileSelected)
  }, [fileSelected])
  return (
    <>
      <Header>{trans.header}</Header>

      <div className="generic__paragraph">{trans.addressDescription}</div>
      <div className="generic__paragraph-with-border">
        {context?.expectedAddress?.street}, {context?.expectedAddress?.postalCode}{' '}
        {context?.expectedAddress?.city}
      </div>

      <div className="generic__paragraph">{trans.description}</div>

      <div className="generic__select-holder">
        <div className="generic__select-holder__label">{trans.document}:</div>
        <SelectInput
          options={documentTypesOptions}
          value={documentTypesOptions[utilityBillTypeId]}
          onChange={(e) => e && !Array.isArray(e) && selectChangeHandle(e as SelectOption)}
        />
      </div>

      <>
        <div
          className={cx('generic__file-upload', fileSelected && 'generic__file-upload-has-file')}
          tabIndex={0}
          role="button"
          onKeyDown={(event) =>
            onKeyDownHandler(event, () => {
              document.getElementById('base64FileInput')?.click()
            })
          }
        >
          <Base64FileInput key={fileInputKey} multiple={false} onChange={getFiles} />
          {fileSelected ? (
            <div className="generic__file-info">
              <div className="generic__file-info__title">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.83333 3.45833C5.21201 3.45833 4.70833 3.96201 4.70833 4.58333V15.4167C4.70833 16.038 5.21201 16.5417 5.83333 16.5417H14.1667C14.788 16.5417 15.2917 16.038 15.2917 15.4167V8.45833H12.5C11.2804 8.45833 10.2917 7.46963 10.2917 6.25V3.45833H5.83333ZM11.375 3.53135V6.25C11.375 6.87132 11.8787 7.375 12.5 7.375H15.2187C15.1627 7.22747 15.0759 7.09191 14.9622 6.9782L11.7718 3.78784C11.6581 3.67412 11.5225 3.58726 11.375 3.53135ZM3.625 4.58333C3.625 3.3637 4.6137 2.375 5.83333 2.375H10.9763C11.562 2.375 12.1237 2.60766 12.5378 3.02181L15.7282 6.21216C16.1423 6.62631 16.375 7.188 16.375 7.77369V15.4167C16.375 16.6363 15.3863 17.625 14.1667 17.625H5.83333C4.6137 17.625 3.625 16.6363 3.625 15.4167V4.58333Z"
                    fill="#303438"
                  />
                </svg>

                <span className="generic__file-info__name">{fileSelected.name}</span>
              </div>
              <span className="generic__file-deselect" onClick={() => resetFileInput()}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.8717 15.6292L10.8603 15.7383C10.8097 15.9851 10.5914 16.1704 10.3297 16.1704C10.0679 16.1704 9.8496 15.9851 9.79907 15.7383L9.78768 15.6292L9.78768 10.7903C9.78768 10.4912 10.0305 10.2483 10.3297 10.2483C10.6288 10.2483 10.8717 10.4912 10.8717 10.7903L10.8717 15.6292Z"
                    fill="#565B60"
                  />
                  <path
                    d="M14.2588 15.6292L14.2474 15.7383C14.1968 15.9851 13.9785 16.1704 13.7168 16.1704C13.455 16.1704 13.2367 15.9851 13.1862 15.7383L13.1748 15.6292V10.7903C13.1748 10.4912 13.4176 10.2483 13.7168 10.2483C14.0159 10.2483 14.2588 10.4912 14.2588 10.7903V15.6292Z"
                    fill="#565B60"
                  />
                  <path
                    d="M6.15865 16.1667V9.08334C6.15865 8.78419 6.40149 8.54135 6.70064 8.54135C6.9998 8.54135 7.24264 8.78419 7.24264 9.08334V16.1667C7.24265 17.7085 8.49219 18.958 10.034 18.958H14.0127C15.5544 18.9579 16.804 17.7084 16.804 16.1667V9.08334C16.804 8.78419 17.0468 8.54135 17.346 8.54135C17.6451 8.54145 17.8872 8.78425 17.8872 9.08334V16.1667C17.8872 18.3067 16.1527 20.0419 14.0127 20.042H10.034C7.89388 20.042 6.15867 18.3068 6.15865 16.1667Z"
                    fill="#565B60"
                  />
                  <path
                    d="M18.7972 6.37734L18.9062 6.38873C19.1532 6.43913 19.3392 6.65747 19.3392 6.91933C19.3392 7.1812 19.1532 7.39954 18.9062 7.44993L18.7972 7.46133H5.24902C4.94987 7.46133 4.70703 7.21849 4.70703 6.91933C4.70703 6.62018 4.94987 6.37734 5.24902 6.37734H18.7972Z"
                    fill="#565B60"
                  />
                  <path
                    d="M12.8073 3.95801C13.6437 3.95801 14.4091 4.43064 14.7832 5.17871L15.4107 6.43522L14.9265 6.67773L14.4422 6.91943L13.814 5.66374C13.6234 5.28261 13.2334 5.04199 12.8073 5.04199H11.2391C10.8664 5.04199 10.5211 5.22577 10.313 5.52702L10.2324 5.66374L9.60419 6.91943L9.11998 6.67773L8.63576 6.43522L9.26321 5.17871L9.33808 5.04199C9.73338 4.37362 10.4549 3.95801 11.2391 3.95801H12.8073Z"
                    fill="#565B60"
                  />
                </svg>
              </span>
            </div>
          ) : (
            <>
              <div className="generic__upload-icon">
                {isFileBeingDragged ? (
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                  >
                    <rect width="48" height="48" rx="10" fill="white" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M24 9C15.7157 9 9 15.7157 9 24C9 32.2843 15.7157 39 24 39C32.2843 39 39 32.2843 39 24C39 15.7157 32.2843 9 24 9ZM11 24C11 16.8203 16.8203 11 24 11C31.1797 11 37 16.8203 37 24C37 31.1797 31.1797 37 24 37C16.8203 37 11 31.1797 11 24Z"
                      fill="var(--icon-main-color, black)"
                    />
                    <rect
                      x="23"
                      y="17"
                      width="2"
                      height="14"
                      rx="1"
                      fill="var(--icon-main-color, black)"
                    />
                    <rect
                      x="31"
                      y="23"
                      width="2"
                      height="14"
                      rx="1"
                      transform="rotate(90 31 23)"
                      fill="var(--icon-main-color, black)"
                    />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.34961 5.5C4.34961 4.03645 5.53605 2.85 6.99961 2.85H13.1712C13.874 2.85 14.548 3.12919 15.045 3.62617L18.8734 7.45459C19.3704 7.95157 19.6496 8.6256 19.6496 9.32843V13.5C19.6496 13.859 19.3586 14.15 18.9996 14.15C18.6406 14.15 18.3496 13.859 18.3496 13.5V10.15H14.9996C13.5361 10.15 12.3496 8.96356 12.3496 7.5V4.15H6.99961C6.25402 4.15 5.64961 4.75442 5.64961 5.5V18.5C5.64961 19.2456 6.25402 19.85 6.99961 19.85H12.4996C12.8586 19.85 13.1496 20.141 13.1496 20.5C13.1496 20.859 12.8586 21.15 12.4996 21.15H6.99961C5.53606 21.15 4.34961 19.9636 4.34961 18.5V5.5ZM13.6496 4.23762V7.5C13.6496 8.24558 14.254 8.85 14.9996 8.85H18.262C18.1949 8.67296 18.0907 8.51029 17.9542 8.37383L14.1258 4.54541C13.9893 4.40895 13.8267 4.30472 13.6496 4.23762ZM16.0095 13.85C16.1861 13.85 16.3551 13.9219 16.4776 14.049L19.3676 17.049C19.6167 17.3076 19.609 17.7191 19.3504 17.9681C19.0919 18.2172 18.6804 18.2095 18.4314 17.951L16.6496 16.1014V20.5C16.6496 20.859 16.3586 21.15 15.9996 21.15C15.6406 21.15 15.3496 20.859 15.3496 20.5V16.1219L13.5876 17.951C13.3385 18.2095 12.9271 18.2172 12.6685 17.9681C12.41 17.7191 12.4023 17.3076 12.6514 17.049L15.5414 14.049C15.6639 13.9219 15.8329 13.85 16.0095 13.85Z"
                      fill="var(--icon-main-color, black)"
                    />
                  </svg>
                )}
              </div>
              <div className="generic__upload-title">
                {isFileBeingDragged ? (
                  <>{trans.dropHere}</>
                ) : (
                  <>
                    <span className="generic__upload-title__link">{trans.clickHereToUpload}</span>{' '}
                    {trans.orDragAndDropYourFiles}
                    <br />
                    <span>
                      {trans.maxFilesize}
                      {trans.maxFilesizeValue} {trans.filetypes}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {fileError && <div className="generic__file-error">{fileError}</div>}
      </>

      <div className="layout__content-container-bottom-fixed layout__content-container-bottom-fixed--gap-16">
        {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
        <Button onClick={() => handleProceed()} disabled={isDisabled}>
          <span className="generic__button-text">{trans.proceed}</span>
        </Button>
      </div>
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </>
  )
})
